import * as _ from 'underscore';
import { Module } from '../Module';
import { IOAuthCredentialTypeInfo } from '../ServerApi/types/IOAuthCredential';
import { ServerService } from '../services/ServerService';

// Use these strings to display info about a particular OAuth provider
export interface IOAuthCredentialDisplayStrings {
  displayName: string;
  promptEmbedCredential: string;
  noCredential: string;
  describeEmbedCredential: string;
  describeNoCredential: string;
}

// Base interface for getting information about what OAuth providers are available on the server
// as well as the behavior of the individual providers
export interface IOAuthCredentialsService {
  getTypeInfo(typeStringKey: string): IOAuthCredentialTypeInfo;
  getAllOAuthCredentialTypes(): Array<IOAuthCredentialTypeInfo>;
  canAddForConnectionType(typeStringKey: string): boolean;
  supportsGenericAuth(typeStringKey: string): boolean;
  supportsTestConnection(typeStringKey: string): boolean;
  getDisplayStrings(typeStringKey: string): IOAuthCredentialDisplayStrings;
}

// Implementation of IOAuthCredentialsService which gets its information from the serverService
export class OAuthCredentialsService implements IOAuthCredentialsService {
  public static $inject = [
    '$translate',
    'ServerService'];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private serverService: ServerService) {
  }

  public getTypeInfo(typeStringKey: string): IOAuthCredentialTypeInfo {
    return _.find(this.getAllOAuthCredentialTypes(), info => info.type === typeStringKey
      || info.type === this.getStorageProviderFromCloudFileClassName(typeStringKey));
  }

  public getAllOAuthCredentialTypes(): Array<IOAuthCredentialTypeInfo> {
    return this.serverService.oauthSettings() || [];
  }

  public canAddForConnectionType(typeStringKey: string): boolean {
    return true; // anything we get back from the server is enabled
  }

  public supportsGenericAuth(typeStringKey: string): boolean {
    const info = this.getTypeInfo(typeStringKey);
    return info ? info.supportsGenericAuth : false;
  }

  public supportsTestConnection(typeStringKey: string): boolean {
    return !this.supportsGenericAuth(typeStringKey);
  }

  public getDisplayStrings(typeStringKey: string): IOAuthCredentialDisplayStrings {
    // Just return the generic description for everything except the provider's name
    const info = this.getTypeInfo(typeStringKey);
    return info && {
        displayName: this.getDisplayNameFromInfo(info),
        promptEmbedCredential : this.$translate.instant('editDataConnections_generic_embedCredential'),
        noCredential: this.$translate.instant('editDataConnections_generic_noCredential'),
        describeEmbedCredential: this.$translate.instant('editDataConnections_generic_describeEmbedCredential'),
        describeNoCredential: this.$translate.instant('editDataConnections_generic_describeNoCredential')
    };
  }

  private getDisplayNameFromInfo(info: IOAuthCredentialTypeInfo): string {
     if (!info || !info.displayNames) {
       return '';
     }

     // info.displayNames will contain a mapping of locale to provider localized name.
     let locale = this.serverService.sessionLocale();
     if (!info.displayNames.hasOwnProperty(locale)) {
       // Just set it to en_US if we don't have the language present
       locale = 'en_US';
     }

     return info.displayNames[locale];
  }

  // For flow keychain, strip provider name from class name if it's a cloud file connection.
  // This is a copy of the native code logic:
  // teams_near/modules/connectors/tabconnect/main/infrastructure/CloudFileConnectorsRegistry.cpp
  private getStorageProviderFromCloudFileClassName(dbClass: string): string {
    if (dbClass.startsWith('cloudfile')) {
      // A full cloudfile class name is like this: `cloudfile:dropbox-textscan`
      const provider = dbClass.split(':')[1].split('-')[0];
      return provider;
    }
    return dbClass;
  }
}

Module.instance().service('OAuthCredentialsService', OAuthCredentialsService);
